import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import useDocumentMetadata from '../hooks/useDocumentMetadata';
import FluidSubpageHero from '../components/FluidSubpageHero';
import { Search, Clock, ArrowRight, BookOpen, Loader2, Sparkles, CheckCircle2, ChevronLeft, ChevronRight } from 'lucide-react';

export default function Vidensbank() {
  const { lang, t, getLocalizedValue, localizePath } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();

  // State driven by URL search params for deep linking and back/forward navigation
  const activeCategory = searchParams.get('category') || 'Alle';
  const searchQuery = searchParams.get('search') || '';
  const rawSide = searchParams.get('side') || searchParams.get('page');
  const page = Math.max(1, parseInt(rawSide || '1', 10) || 1);

  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cmsPage, setCmsPage] = useState(null);

  const limit = 6;

  // SEO Metadata
  const seoTitle = lang === 'da' 
    ? 'Viden - Artikler og guides | Nordisk Psykiatri' 
    : 'Knowledge base - Articles and guides | Nordisk Psykiatri';

  const seoDesc = lang === 'da'
    ? 'Guides og artikler skrevet af vores læge og psykolog. Læs om udredning, ADHD, autisme, angst, depression og psykoterapi.'
    : 'Guides and articles written by our doctor and psychologist. Read about assessment, ADHD, autism, anxiety, depression and therapy.';

  useDocumentMetadata(seoTitle, seoDesc, { lang });

  // Category mapping for cross-language filtering
  const categoryMapping = {
    'Alle': { da: 'Alle', en: 'All', sv: 'All' },
    'All': { da: 'Alle', en: 'All', sv: 'All' },
    'Alla': { da: 'Alle', en: 'All', sv: 'All' },
    'ADHD': { da: 'ADHD', en: 'ADHD', sv: 'ADHD' },
    'Autisme': { da: 'Autisme', en: 'Autism', sv: 'Autism' },
    'Autism': { da: 'Autisme', en: 'Autism', sv: 'Autism' },
    'Udredning': { da: 'Udredning', en: 'Assessment', sv: 'Assessment' },
    'Assessment': { da: 'Udredning', en: 'Assessment', sv: 'Assessment' },
    'Utredning': { da: 'Udredning', en: 'Assessment', sv: 'Assessment' },
    'Behandling': { da: 'Behandling', en: 'Treatment', sv: 'Treatment' },
    'Treatment': { da: 'Behandling', en: 'Treatment', sv: 'Treatment' },
    'Angst': { da: 'Angst', en: 'Anxiety', sv: 'Anxiety' },
    'Anxiety': { da: 'Angst', en: 'Anxiety', sv: 'Anxiety' },
    'Ångest': { da: 'Angst', en: 'Anxiety', sv: 'Anxiety' },
    'Depression': { da: 'Depression', en: 'Depression', sv: 'Depression' },
    'OCD': { da: 'OCD', en: 'OCD', sv: 'OCD' },
    'PTSD': { da: 'PTSD', en: 'PTSD', sv: 'PTSD' },
    'Personlighedsforstyrrelser': { da: 'Personlighedsforstyrrelser', en: 'Personality disorders', sv: 'Personality disorders' },
    'Personality disorders': { da: 'Personlighedsforstyrrelser', en: 'Personality disorders', sv: 'Personality disorders' },
    'Personlighetsstörningar': { da: 'Personlighedsforstyrrelser', en: 'Personality disorders', sv: 'Personality disorders' }
  };

  const getCategoryCount = (catName) => {
    if (catName === 'Alle' || catName === 'All' || catName === 'Alla') {
      return articles.length;
    }
    const mapped = categoryMapping[catName];
    if (!mapped) return 0;
    return articles.filter(article => {
      const articleCatDa = article.kategori?.da || article.category?.da || article.terms?.kategori?.[0] || '';
      const articleCatSv = article.kategori?.sv || article.category?.sv || article.terms?.kategori?.[0] || '';
      const termsList = article.terms?.kategori || [];

      if (lang === 'da') {
        return articleCatDa.toLowerCase() === mapped.da.toLowerCase() || 
               termsList.some(t => t.toLowerCase() === mapped.da.toLowerCase());
      } else {
        return articleCatSv.toLowerCase() === mapped.en.toLowerCase() || 
               articleCatSv.toLowerCase() === mapped.da.toLowerCase() ||
               termsList.some(t => t.toLowerCase() === mapped.en.toLowerCase() || t.toLowerCase() === mapped.da.toLowerCase());
      }
    }).length;
  };

  const categories = lang === 'da' 
    ? [
        { name: 'Alle', count: getCategoryCount('Alle') },
        { name: 'ADHD', count: getCategoryCount('ADHD') },
        { name: 'Autisme', count: getCategoryCount('Autisme') },
        { name: 'Udredning', count: getCategoryCount('Udredning') },
        { name: 'Behandling', count: getCategoryCount('Behandling') },
        { name: 'Angst', count: getCategoryCount('Angst') },
        { name: 'Depression', count: getCategoryCount('Depression') },
        { name: 'OCD', count: getCategoryCount('OCD') },
        { name: 'PTSD', count: getCategoryCount('PTSD') }
      ]
    : [
        { name: 'All', count: getCategoryCount('All') },
        { name: 'ADHD', count: getCategoryCount('ADHD') },
        { name: 'Autism', count: getCategoryCount('Autism') },
        { name: 'Assessment', count: getCategoryCount('Assessment') },
        { name: 'Treatment', count: getCategoryCount('Treatment') },
        { name: 'Anxiety', count: getCategoryCount('Anxiety') },
        { name: 'Depression', count: getCategoryCount('Depression') },
        { name: 'OCD', count: getCategoryCount('OCD') },
        { name: 'PTSD', count: getCategoryCount('PTSD') }
      ];

  useEffect(() => {
    const fetchArticles = async () => {
      setLoading(true);
      setError(null);
      try {
        let res = await fetch(`/_api/public/v2/content/artikler?limit=100&sort=updatedAt&order=desc`);
        if (!res.ok) {
          throw new Error('Kunne ikke hente artikler');
        }
        const data = await res.json();
        setArticles(data.items || []);
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    const fetchPage = async () => {
      try {
        const res = await fetch('/_api/public/content/vidensbank_side');
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data) && data.length > 0) {
            setCmsPage(data[0]);
          }
        }
      } catch (err) {
        console.error('Error fetching vidensbank_side:', err);
      }
    };

    fetchPage();
    fetchArticles();
  }, [lang]);

  // URL helper for pagination links
  const getPageUrl = (targetPage) => {
    const params = new URLSearchParams(searchParams);
    params.delete('page');
    if (targetPage <= 1) {
      params.delete('side');
    } else {
      params.set('side', targetPage.toString());
    }
    const query = params.toString();
    const basePath = localizePath('/vidensbank');
    return query ? `${basePath}?${query}` : basePath;
  };

  // Handle category change
  const handleCategoryChange = (catName) => {
    const newParams = new URLSearchParams(searchParams);
    if (catName === 'Alle' || catName === 'All' || catName === 'Alla') {
      newParams.delete('category');
    } else {
      newParams.set('category', catName);
    }
    newParams.delete('side');
    newParams.delete('page');
    setSearchParams(newParams);
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const query = e.target.value;
    const newParams = new URLSearchParams(searchParams);
    if (query) {
      newParams.set('search', query);
    } else {
      newParams.delete('search');
    }
    newParams.delete('side');
    newParams.delete('page');
    setSearchParams(newParams);
  };

  // Filter articles based on active category and search query
  const filteredArticles = articles.filter(article => {
    // Category Filter
    if (activeCategory !== 'Alle' && activeCategory !== 'Alla') {
      const mapped = categoryMapping[activeCategory];
      const articleCatDa = article.kategori?.da || article.category?.da || article.terms?.kategori?.[0] || '';
      const articleCatSv = article.kategori?.sv || article.category?.sv || article.terms?.kategori?.[0] || '';
      const termsList = article.terms?.kategori || [];
      
      const matchDa = articleCatDa.toLowerCase() === mapped.da.toLowerCase() || 
                      termsList.some(t => t.toLowerCase() === mapped.da.toLowerCase());
      const matchSv = articleCatSv.toLowerCase() === mapped.sv.toLowerCase() || 
                      termsList.some(t => t.toLowerCase() === mapped.sv.toLowerCase());

      if (lang === 'da') {
        if (!matchDa) return false;
      } else {
        if (!matchSv) return false;
      }
    }

    // Search Filter
    if (!searchQuery) return true;
    const title = getLocalizedValue(article.titel || article.title).toLowerCase();
    const summary = getLocalizedValue(article.resume || article.summary).toLowerCase();
    const query = searchQuery.toLowerCase();
    return title.includes(query) || summary.includes(query);
  });

  // Paginate sorted articles
  const total = filteredArticles.length;
  const totalPages = Math.ceil(total / limit);
  const paginatedArticles = filteredArticles.slice((page - 1) * limit, page * limit);

  // Decorative Abstract SVG illustrations for article cards
  const renderAbstractIllustration = (index, category) => {
    const cleanCat = (category || '').toLowerCase();
    if (cleanCat.includes('adhd')) {
      return (
        <svg viewBox="0 0 100 60" className="w-16 h-10 text-petrol-dark/40 stroke-current fill-none stroke-[1.5]">
          <path d="M15,30 Q30,10 50,30 T85,30" strokeDasharray="3 3" />
          <circle cx="50" cy="30" r="14" />
          <circle cx="50" cy="30" r="6" className="fill-petrol-dark/10" />
          <path d="M30,30 L70,30 M50,10 L50,50" />
        </svg>
      );
    }
    if (cleanCat.includes('autis')) {
      return (
        <svg viewBox="0 0 100 60" className="w-16 h-10 text-petrol-dark/40 stroke-current fill-none stroke-[1.5]">
          <rect x="25" y="15" width="22" height="22" rx="4" />
          <rect x="53" y="23" width="22" height="22" rx="4" className="fill-petrol-dark/10" />
          <circle cx="36" cy="26" r="3" className="fill-petrol-dark/40" />
          <circle cx="64" cy="34" r="3" className="fill-petrol-dark/40" />
        </svg>
      );
    }
    if (cleanCat.includes('behand') || cleanCat.includes('terapi')) {
      return (
        <svg viewBox="0 0 100 60" className="w-16 h-10 text-petrol-dark/40 stroke-current fill-none stroke-[1.5]">
          <path d="M20,40 C35,15 65,15 80,40" />
          <path d="M30,42 C42,26 58,26 70,42" />
          <circle cx="50" cy="22" r="4" className="fill-petrol-dark/20" />
        </svg>
      );
    }
    if (cleanCat.includes('udred')) {
      return (
        <svg viewBox="0 0 100 60" className="w-16 h-10 text-petrol-dark/40 stroke-current fill-none stroke-[1.5]">
          <circle cx="35" cy="30" r="16" />
          <circle cx="65" cy="30" r="16" />
          <path d="M40,30 L60,30" strokeWidth="2" />
          <circle cx="50" cy="30" r="3" className="fill-petrol-dark/40" />
        </svg>
      );
    }
    // Default abstract geometry
    return (
      <svg viewBox="0 0 100 60" className="w-16 h-10 text-petrol-dark/40 stroke-current fill-none stroke-[1.5]">
        <circle cx="32" cy="30" r="14" />
        <line x1="46" y1="30" x2="68" y2="30" />
        <rect x="68" y="20" width="20" height="20" rx="3" className="fill-petrol-dark/10" />
      </svg>
    );
  };

  const heroBadge = cmsPage ? getLocalizedValue(cmsPage.hero_badge) || (lang === 'da' ? 'VIDENSBANK' : 'KUNSKAPSBANK') : (lang === 'da' ? 'VIDENSBANK' : 'KUNSKAPSBANK');
  const heroTitle = cmsPage ? getLocalizedValue(cmsPage.hero_overskrift) || (lang === 'da' ? 'Viden og artikler' : 'Kunskap och artiklar') : (lang === 'da' ? 'Viden og artikler' : 'Kunskap och artiklar');
  const heroDesc = cmsPage && getLocalizedValue(cmsPage.hero_beskrivelse)
    ? getLocalizedValue(cmsPage.hero_beskrivelse)
    : (
        lang === 'da' ? (
          <>
            Guides og artikler skrevet af vores læge og psykolog.
            <br />
            Læs om udredning, diagnoser og evidensbaseret behandling.
          </>
        ) : (
          <>
            Guides and articles written by our physician and psychologist.
            <br />
            Learn about assessment, diagnoses, and evidence-based treatment.
          </>
        )
      );
  const heroImage = (cmsPage && cmsPage.hero_billede && cmsPage.hero_billede[0])
    ? cmsPage.hero_billede[0]
    : "https://assets.auxodev.dk/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/image_7b1fdfcc.png";

  return (
    <div className="bg-sand-light min-h-screen">
      
      {/* 1. HERO SECTION (Standard FluidSubpageHero with background image) */}
      <FluidSubpageHero
        eyebrow={heroBadge}
        title={heroTitle}
        description={heroDesc}
        bgImageUrl={heroImage}
        bgImageAlt={lang === 'da' ? 'Nordisk Psykiatri vidensbank' : 'Nordisk Psykiatri knowledge base'}
        showBgOverlay={false}
      />

      {/* 2. INTERACTIVE SCREENING HIGHLIGHTS */}
      <section className="pt-8 sm:pt-10 pb-4 px-6">
        <div className="mx-auto max-w-[1100px] grid grid-cols-1 sm:grid-cols-2 gap-4">
          
          <Link
            to={localizePath('/vidensbank/mistanke-om-adhd')}
            className="group flex items-center justify-between p-5 rounded-2xl bg-[#253a35] border border-[#253a35] hover:bg-[#1e2f2b] hover:shadow-md transition-all duration-300"
          >
            <div className="pr-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] font-bold tracking-wider text-[#253a35] uppercase bg-[#D1E1BE] px-2 py-0.5 rounded-full">
                  {lang === 'da' ? 'SELVTEST' : 'SELF-TEST'}
                </span>
                <span className="text-xs text-white/60 font-medium">ASRS-v1.1</span>
              </div>
              <h3 className="font-display text-lg text-white font-medium group-hover:text-[#D1E1BE] transition-colors">
                {lang === 'da' ? 'Mistanke om ADHD?' : 'Suspect ADHD?'}
              </h3>
              <p className="text-xs text-white/75 mt-0.5 line-clamp-1">
                {lang === 'da' ? 'Tag den officielle screening på 5 minutter' : 'Take the official screening in 5 minutes'}
              </p>
            </div>
            <div className="w-8 h-8 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white group-hover:bg-white group-hover:text-[#253a35] transition-all duration-300 shrink-0">
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" />
            </div>
          </Link>

          <Link
            to={localizePath('/vidensbank/autisme-screening')}
            className="group flex items-center justify-between p-5 rounded-2xl bg-[#253a35] border border-[#253a35] hover:bg-[#1e2f2b] hover:shadow-md transition-all duration-300"
          >
            <div className="pr-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] font-bold tracking-wider text-[#253a35] uppercase bg-[#D1E1BE] px-2 py-0.5 rounded-full">
                  {lang === 'da' ? 'SELVTEST' : 'SELF-TEST'}
                </span>
                <span className="text-xs text-white/60 font-medium">RAADS-R</span>
              </div>
              <h3 className="font-display text-lg text-white font-medium group-hover:text-[#D1E1BE] transition-colors">
                {lang === 'da' ? 'Autisme-screening' : 'Autism Screening'}
              </h3>
              <p className="text-xs text-white/75 mt-0.5 line-clamp-1">
                {lang === 'da' ? 'Test tegn på autismespektrum hos voksne' : 'Test signs of autism spectrum in adults'}
              </p>
            </div>
            <div className="w-8 h-8 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white group-hover:bg-white group-hover:text-[#253a35] transition-all duration-300 shrink-0">
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" />
            </div>
          </Link>

        </div>
      </section>

      {/* 4. ARTICLE FEED / LIST (2-column layout matching alethia.dk) */}
      <main className="py-6 sm:py-8 px-6">
        <div className="mx-auto max-w-[1100px]">
          
          {loading ? (
            <div className="flex flex-col items-center justify-center py-24 gap-4">
              <Loader2 className="w-8 h-8 text-petrol-medium animate-spin" />
              <p className="text-xs sm:text-sm text-text-dark/60 font-medium">{t('common.loading')}</p>
            </div>
          ) : error ? (
            <div className="text-center py-24">
              <p className="text-red-600 font-medium">{t('common.error_loading')}</p>
            </div>
          ) : paginatedArticles.length === 0 ? (
            <div className="text-center py-20 bg-[#FAF8F5] rounded-3xl border border-sand-medium/60 p-8 my-6">
              <BookOpen className="w-10 h-10 text-text-dark/30 mx-auto mb-3" />
              <p className="text-text-dark/70 text-sm font-medium">
                {lang === 'da' ? 'Ingen artikler fundet, der matcher din søgning.' : 'No articles found matching your search.'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-petrol-dark/10">
              {paginatedArticles.map((article, index) => {
                const title = getLocalizedValue(article.titel || article.title);
                const summary = getLocalizedValue(article.resume || article.summary);
                const author = getLocalizedValue(article.forfatter || article.author) || (lang === 'da' ? 'Nordisk Psykiatri' : 'Nordisk Psykiatri');
                const readTime = getLocalizedValue(article.laesetid || article.read_time) || (lang === 'da' ? '5 min læsning' : '5 min read');
                const categoryTerm = article.terms?.kategori?.[0] || getLocalizedValue(article.kategori || article.category) || 'Viden';
                const articleSlug = (typeof article.slug === 'object'
                  ? (getLocalizedValue(article.slug) || article.slug?.da || article.slug?.sv)
                  : article.slug) || article.id;
                const articlePath = localizePath(`/vidensbank/${articleSlug}`);
                
                const rawImage = article.billede?.[0] || article.image?.[0];
                const imageUrl = rawImage 
                  ? (rawImage.includes('assets.auxodev.dk') && !rawImage.includes('/cdn-cgi/image/')
                      ? `https://assets.auxodev.dk/cdn-cgi/image/width=480,quality=80,format=auto/assets/${rawImage.split('/assets/')[1]}`
                      : rawImage)
                  : null;

                return (
                  <article key={article.id} className="py-8 sm:py-10 first:pt-4">
                    <Link
                      to={articlePath}
                      className="group flex flex-col-reverse sm:flex-row justify-between items-start sm:items-center gap-6 sm:gap-10 transition-colors"
                    >
                      {/* Left Column: Text & Meta (~75% width) */}
                      <div className="flex-1 min-w-0 pr-0 sm:pr-4">
                        
                        {/* Title with Arrow */}
                        <h2 className="font-display text-2xl sm:text-[28px] md:text-3xl font-normal text-petrol-dark group-hover:text-petrol-medium transition-colors leading-[1.25] flex items-baseline gap-2">
                          <span>{title}</span>
                          <span className="inline-block text-petrol-dark/70 font-sans text-xl group-hover:translate-x-1.5 transition-transform duration-300 shrink-0">
                            →
                          </span>
                        </h2>

                        {/* Excerpt */}
                        {summary && (
                          <p className="text-sm sm:text-[15px] text-text-dark/75 mt-2.5 leading-relaxed font-sans line-clamp-3">
                            {summary}
                          </p>
                        )}

                        {/* Meta Info */}
                        <div className="text-xs text-text-dark/50 mt-3.5 flex flex-wrap items-center gap-2 font-sans">
                          <span>{author}</span>
                          <span>·</span>
                          <span>{readTime}</span>
                          {categoryTerm && (
                            <>
                              <span>·</span>
                              <span className="capitalize text-petrol-dark/60 font-medium">
                                {categoryTerm}
                              </span>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Right Column: Visual Thumbnail Box (~25% width) */}
                      <div 
                        className="w-full sm:w-44 md:w-52 h-32 sm:h-28 md:h-32 rounded-2xl bg-[#CFE0DB] overflow-hidden shrink-0 flex items-center justify-center border border-petrol-dark/10 group-hover:border-petrol-dark/25 transition-all duration-300 relative shadow-none"
                        style={{ backgroundColor: '#CFE0DB' }}
                      >
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center p-4 group-hover:scale-105 transition-transform duration-500">
                            {renderAbstractIllustration(index, categoryTerm)}
                          </div>
                        )}
                      </div>

                    </Link>
                  </article>
                );
              })}
            </div>
          )}

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="pt-12 pb-6 border-t border-petrol-dark/10 flex items-center justify-between">
              {page > 1 ? (
                <Link
                  to={getPageUrl(page - 1)}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-petrol-dark hover:text-petrol-medium py-2 px-3 rounded-lg hover:bg-sand-medium/30 transition-all"
                >
                  <ChevronLeft className="w-4 h-4" />
                  {lang === 'da' ? 'Forrige' : 'Previous'}
                </Link>
              ) : (
                <span
                  className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-petrol-dark opacity-30 pointer-events-none py-2 px-3 rounded-lg transition-all"
                  aria-disabled="true"
                >
                  <ChevronLeft className="w-4 h-4" />
                  {lang === 'da' ? 'Forrige' : 'Previous'}
                </span>
              )}

              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => {
                  if (page === num) {
                    return (
                      <span
                        key={num}
                        aria-current="page"
                        className="w-8 h-8 rounded-full text-xs font-semibold transition-all flex items-center justify-center bg-petrol-dark text-white select-none"
                      >
                        {num}
                      </span>
                    );
                  }
                  return (
                    <Link
                      key={num}
                      to={getPageUrl(num)}
                      className="w-8 h-8 rounded-full text-xs font-semibold transition-all flex items-center justify-center text-petrol-dark/70 hover:bg-sand-medium/40 hover:text-petrol-dark"
                    >
                      {num}
                    </Link>
                  );
                })}
              </div>

              {page < totalPages ? (
                <Link
                  to={getPageUrl(page + 1)}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-petrol-dark hover:text-petrol-medium py-2 px-3 rounded-lg hover:bg-sand-medium/30 transition-all"
                >
                  {lang === 'da' ? 'Næste' : 'Next'}
                  <ChevronRight className="w-4 h-4" />
                </Link>
              ) : (
                <span
                  className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-petrol-dark opacity-30 pointer-events-none py-2 px-3 rounded-lg transition-all"
                  aria-disabled="true"
                >
                  {lang === 'da' ? 'Næste' : 'Next'}
                  <ChevronRight className="w-4 h-4" />
                </span>
              )}
            </div>
          )}

        </div>
      </main>

    </div>
  );
}
